// The one element injected into Settings → general: an "experimental" section
// with the YouTube-mode switch. Renders nothing unless the server says the
// user is allowlisted. The flag is stored per user on the server, so it
// follows the user across devices.

import { useState } from "react";

import { apiJson } from "../api/client";
import { loadYtAutoplay, saveYtAutoplay, useYtAccess, ytKeys, type YtAccess } from "./api";

import "./youtube.css";

export function YoutubeSettingsSection() {
  const { data, mutate } = useYtAccess();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [autoplay, setAutoplay] = useState<boolean>(loadYtAutoplay);

  if (!data?.eligible) return null;

  async function toggle() {
    if (busy || !data) return;
    setBusy(true);
    setNote(null);
    try {
      const updated = await apiJson<YtAccess>(ytKeys.access, {
        method: "PUT",
        body: JSON.stringify({ enabled: !data.enabled }),
      });
      await mutate(updated, { revalidate: false });
    } catch {
      setNote("saving failed — try again");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="settings__section yt-settings-section">
      <h2 className="settings__section-title">experimental</h2>
      <div className="settings__row">
        <div className="settings__row-text">
          <div className="settings__row-label">youtube mode</div>
          <div className="settings__row-desc">
            a second, separate app over your own youtube watch history: fill a queue with 100
            random videos you have watched, play them in place, and rate their relevance. own
            lists, own database — nothing here touches the film lists. adds a “youtube” button
            to the top bar.
          </div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={data.enabled}
          className={"switch" + (data.enabled ? " switch--on" : "")}
          onClick={() => void toggle()}
          disabled={busy}
          aria-label="enable youtube mode"
        >
          <span className="switch__knob" />
        </button>
      </div>
      <div className="settings__row yt-settings-row">
        <div className="settings__row-text">
          <div className="settings__row-label">autoplay</div>
          <div className="settings__row-desc">
            start each video as soon as you select it, so rating a queue is one key per
            video. your browser may still require a click on the player the first time.
            stored in this browser; takes effect when you return to the navigator.
          </div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={autoplay}
          className={"switch" + (autoplay ? " switch--on" : "")}
          onClick={() => {
            const next = !autoplay;
            setAutoplay(next);
            saveYtAutoplay(next);
          }}
          aria-label="autoplay videos"
        >
          <span className="switch__knob" />
        </button>
      </div>

      {!data.source_available && (
        <div className="yt-settings-warn" role="alert">
          the youtube history database is not present on the server (youtube.db) — the mode
          will show 503 errors until it is copied into the data directory.
        </div>
      )}
      {note && <div className="yt-settings-warn">{note}</div>}
    </section>
  );
}
